import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, Globe } from 'lucide-react';
import eyecontrolLogo from '@/assets/eyecontrol-logo.png';
import { useCaregiverTranslation, type CaregiverLanguage } from '@/hooks/useCaregiverTranslation';
import type { TranslationKey } from '@/lib/translations';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const LANGUAGE_OPTIONS: { value: CaregiverLanguage; label: string }[] = [
  { value: 'he', label: 'עברית' },
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'العربية' },
  { value: 'ru', label: 'Русский' },
];

const NotificationReceived = () => {
  const [searchParams] = useSearchParams();
  const { t, lang, dir, setLanguage } = useCaregiverTranslation();
  const direction = dir as 'rtl' | 'ltr';

  const [patientName, setPatientName] = useState<string>(
    searchParams.get('name') || ''
  );
  const messageBody = searchParams.get('body') || '';

  // Replace {patientName} placeholder
  const renderBody = () => {
    const text = t('notificationReceivedBody' as TranslationKey);
    return text.replace('{patientName}', patientName || t('theUser' as TranslationKey));
  };

  // Update document title
  useEffect(() => {
    document.title = t('notificationReceivedTitle' as TranslationKey);
  }, [t, lang]);

  return (
    <div
      className="fixed inset-0 bg-gradient-to-b from-background to-muted flex flex-col items-center justify-center p-4 overflow-y-auto"
      dir={direction}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
            <Bell className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-xl">
            {t('notificationReceivedTitle' as TranslationKey)}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {messageBody && (
            <div className="p-4 bg-primary/10 border border-primary/30 rounded-lg">
              <p className="text-muted-foreground text-center text-base mb-2">
                {t('notificationMessageLabel' as TranslationKey)}
              </p>
              <p className="text-xl text-foreground text-center whitespace-pre-wrap leading-relaxed break-words">
                {messageBody}
              </p>
            </div>
          )}
          <p className="text-lg text-foreground text-center leading-relaxed whitespace-pre-wrap">
            {renderBody()}
          </p>

          <div className="!mt-10 p-4 bg-muted/60 rounded-lg border border-border space-y-3">
            <p className="font-semibold text-foreground text-lg">
              {t('notificationStopHeader' as TranslationKey)}
            </p>
            <p className="text-muted-foreground leading-relaxed text-lg">
              {t('notificationStopAndroid' as TranslationKey)}
            </p>
            <p className="text-muted-foreground leading-relaxed text-lg">
              {t('notificationStopIOS' as TranslationKey)}
            </p>
          </div>

          <p className="text-muted-foreground text-center text-base">
            {t('notificationCloseHint' as TranslationKey)}
          </p>

          <div className="w-24 h-24 mx-auto flex items-center justify-center pt-8">
            <img
              src={eyecontrolLogo}
              alt="EyeControl"
              className="w-full h-full object-contain"
            />
          </div>
        </CardContent>
      </Card>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            className="fixed bottom-4 right-4 w-12 h-12 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground shadow-lg border-0"
            aria-label="Change language"
          >
            <Globe className="w-5 h-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[120px]">
          {LANGUAGE_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => setLanguage(option.value)}
              className={lang === option.value ? 'bg-accent' : ''}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default NotificationReceived;
